import {
  CheckCircle,
  XCircle,
  Calendar,
  Info,
  Phone,
  ArrowRightLeft,
  CalendarX,
} from 'lucide-react';
import { statusLabel } from '../i18n/tr';

const MTS_SKS_TARGETS = new Set(['mts', 'sks']);

function isAppointmentConfirmed(appt) {
  if (!appt) return false;
  return appt.confirmed === true || appt.status === 'confirmed';
}

function hasBookedAppointmentFields(appt) {
  if (!appt) return false;
  return Boolean(appt.date && appt.time && appt.plate);
}

function routingStatusFromTarget(targetId) {
  if (!targetId) return null;
  const id = String(targetId).toLowerCase();
  if (MTS_SKS_TARGETS.has(id) || id.includes('mts') || id.includes('sks')) {
    return 'Routed to MTS/SKS';
  }
  return 'Transferred';
}

const BOOKING_PHRASES = [
  'randevu oluştur',
  'randevu kayd',
  'randevu onay',
  'appointment created',
  'successfully booked',
  'booked a',
  'booked an',
  'maintenance appointment',
  'periodic maintenance',
];

/** Mirror server resolveCallStatus for display of stored + legacy rows */
export function resolveDisplayStatus(call) {
  if (!call) return 'Info Provided';

  const { summary = '', appointment, transfer, status: storedStatus } = call;
  const s = (summary || '').toLowerCase();

  if (storedStatus === 'Failed') return 'Failed';

  const transferTarget = transfer?.targetId || appointment?.routedTo;
  if (transferTarget) {
    const route = routingStatusFromTarget(transferTarget);
    if (route) return route;
  }

  if (storedStatus === 'Routed to MTS/SKS' || storedStatus === 'Transferred') {
    return storedStatus;
  }

  if (isAppointmentConfirmed(appointment)) return 'Appointment Created';
  if (hasBookedAppointmentFields(appointment) && appointment.status !== 'cancelled') {
    return 'Appointment Created';
  }

  if (storedStatus === 'Appointment Created') return 'Appointment Created';

  if (BOOKING_PHRASES.some((p) => s.includes(p))) return 'Appointment Created';

  if (storedStatus === 'Appointment Requested') return 'Appointment Requested';
  if (s.includes('randevu') || s.includes('appointment')) return 'Appointment Requested';

  if (storedStatus === 'Info Provided') return 'Info Provided';
  if (s.includes('bilgi')) return 'Info Provided';

  if (storedStatus === 'Success') {
    if (isAppointmentConfirmed(appointment) || hasBookedAppointmentFields(appointment)) {
      return 'Appointment Created';
    }
    return 'Info Provided';
  }

  if (storedStatus && storedStatus !== 'Success') return storedStatus;

  return 'Info Provided';
}

export function getStatusStyle(status) {
  switch (status) {
    case 'Success':
      return 'text-emerald-300 bg-emerald-400/10 ring-emerald-400/25';
    case 'Failed':
      return 'text-rose-300 bg-rose-500/10 ring-rose-400/25';
    case 'Appointment Created':
      return 'text-sky-300 bg-sky-400/10 ring-sky-400/25';
    case 'Appointment Requested':
      return 'text-orange-300 bg-orange-400/10 ring-orange-400/25';
    case 'Info Provided':
      return 'text-violet-300 bg-violet-400/10 ring-violet-400/25';
    case 'Routed to MTS/SKS':
      return 'text-amber-300 bg-amber-400/10 ring-amber-400/25';
    case 'Transferred':
      return 'text-indigo-300 bg-indigo-400/10 ring-indigo-400/25';
    default:
      return 'text-slate-300 bg-white/5 ring-white/15';
  }
}

export function getStatusIcon(status, className = 'w-4 h-4') {
  switch (status) {
    case 'Success':
      return <CheckCircle className={`${className} text-emerald-300`} />;
    case 'Failed':
      return <XCircle className={`${className} text-rose-300`} />;
    case 'Appointment Created':
      return <Calendar className={`${className} text-sky-300`} />;
    case 'Appointment Requested':
      return <CalendarX className={`${className} text-orange-300`} />;
    case 'Info Provided':
      return <Info className={`${className} text-violet-300`} />;
    case 'Routed to MTS/SKS':
    case 'Transferred':
      return <ArrowRightLeft className={`${className} text-amber-300`} />;
    default:
      return <Phone className={`${className} text-slate-400`} />;
  }
}

export { statusLabel };
